/**
 * Class that attempts to represent a Box (or shelf) and load its information onto the application
 * state.
 *
 * ---------------------------------
 * Notes on initial state and future
 * ---------------------------------
 *
 * This is first version which is basically an extract from the success promise handler
 * that was located in _getNextSortBxo -> Endpoints.getNextSortBox().then(..)
 *
 * - 'appContext': refers to the 'this' from the App root, needed to set state or retrieve state
 * values.
 *
 * - 'sortBoxModel': is the response received fro the server.
 *
 * Just to mention some key elements introduced here.
 *
 * Now...
 * The idea is to keep improving on this, for instance split the logic into various functions or
 * use the redux store already available to handle state to mention some.
 */

import * as Constants from "../utils/Constants";
import { getTextByConstant } from "../utils/TextConfigUtil";
import * as UtilMethods from "../utils/UtilMethods";

import {
  FINISH_NOT_FOUND_NO_PERMISSION_ERROR_CODE,
  FINISH_NOT_FOUND_RX_SINGLE_BLOCKED_ERROR_CODE,
  FINISH_NOT_FOUND_RX_SINGEL_NOT_FOUND_ERROR_CODE,
  FINISH_NOT_FOUND_FINALIZE_SORT_MODAL_OPEN_ERROR_CODE,
  FINISH_NOT_FOUND_HAS_INPUT_WAS_AFFECTED_BY_OFFSET_ERROR_CODE,
  FINISH_NOT_FOUND_WAS_AFFECTED_BY_OFFSET_ERROR_CODE,
  FINISH_NOT_FOUND_ARTICLE_DOES_NOT_BELONG_TO_PICKLIST_ERROR_CODE,
  FINISH_NOT_FOUND_STATION_NOT_ALLOWED_ERROR_CODE,
  FINISH_NOT_FOUND_MULTIPLE_SORTS_CART,
} from "../utils/constants/errorCodes";
import { WARNING_MODAL } from "../utils/constants/componentConstants";
import SharedFunctions from "../utils/SharedFunctions";
import { replaceTextTemplate } from "../utils/stringUtil";
import { el } from "date-fns/locale";

const { ActionTypes, SortBoxModelReturnType } = Constants;

export default class SortBox {
  input = void 0;
  appContext = void 0;
  sortBoxModel = void 0;
  forcedShelf = void 0;

  constructor(input, forcedShelf, sortBoxModel, appContext) {
    this.input = input;
    this.sortBoxModel = sortBoxModel;
    this.forcedShelf = forcedShelf;
    this.appContext = appContext;
  }

  // TODO (Refactor/Enhancement) Alternatively this could be a static method.
  load() {
    const {
      notFound,
      returnType,
      instruction,
      wasAffectedByOffset,
      warehouseComment,
      sortBoxItems,
      sortBoxID,
      shelfNumber,
      orderNumber,
      wasCancelled,
      isPaused,
      scanID,
      prioDeadlineId,
      cartContent,
      companyID,
      shopID,
      isUrgent,
      workstationType,
      partialDelivery,
      isSingle,
      partialDeliveryAllowed,
      invoiceNumber,
      multipleSortsCount
    } = this.sortBoxModel;
    if (notFound) {
      // Det finns ingen mer att köra på denna vagn, måste begära en ny
      const finishNotFound = (
        clearCart,
        errorCode,
        warningMessage = "",
        warningHeader = "",
        autoClose = false
      ) => {
        if (clearCart) {
          this.appContext._clearCartState();
          this.appContext.props.clearCartState();
          this.appContext._performNextAction();
        }
        this.appContext._showWarningModal(warningMessage, errorCode, warningHeader, "", autoClose);
        this.appContext.setState({
          scanDisabled: false,
          pendingInstruction: "",
        });
        this.appContext.props.toggleLoading(false);
      };

      const warningMessage =
        (returnType && getTextByConstant(SortBoxModelReturnType, returnType)) || "";
      const warningHeader =
        (returnType && getTextByConstant(SortBoxModelReturnType, returnType, "header")) || "";

      switch (returnType) {
        case SortBoxModelReturnType.MULTIPLE_SORTS_CART:
          const replacements = {
            multipleSortsCount
          };
          const message = replaceTextTemplate(warningMessage, replacements);
          return finishNotFound(
            true, 
            FINISH_NOT_FOUND_MULTIPLE_SORTS_CART,
            message,
            warningHeader
          )
        case SortBoxModelReturnType.NO_PERMISSION:
        case SortBoxModelReturnType.NOONE_IN_CHARGE:
          return finishNotFound(
            true,
            FINISH_NOT_FOUND_NO_PERMISSION_ERROR_CODE,
            warningMessage,
            warningHeader
          );
        case SortBoxModelReturnType.STATION_NOT_ALLOWED:
          return finishNotFound(
            true,
            FINISH_NOT_FOUND_STATION_NOT_ALLOWED_ERROR_CODE,
            warningMessage,
            warningHeader
          );
        case SortBoxModelReturnType.PACKING_NOT_ALLOWED:
          this.appContext._signOut(() => this.appContext.props.setPackingAllowed(false));
          return;
        case SortBoxModelReturnType.RX_SINGLE_BLOCKED:
          this.appContext.props.setRXEnabled(false);
          return finishNotFound(
            true,
            FINISH_NOT_FOUND_RX_SINGLE_BLOCKED_ERROR_CODE,
            warningMessage,
            warningHeader
          );
        case SortBoxModelReturnType.RX_SINGEL_NOT_FOUND:
          this.appContext.props.setRXEnabled(false);
          return finishNotFound(
            false,
            FINISH_NOT_FOUND_RX_SINGEL_NOT_FOUND_ERROR_CODE,
            warningMessage,
            warningHeader
          );
        default:
          break;
      }

      if (instruction && instruction.visible) {
        this.appContext.props.setCurrentInstruction(instruction, () =>
          this.appContext._showWorkstationInstruction()
        );
      }

      if (this.input && this.input.length) {
        if (this.appContext.state.finalizeSortModalOpen) {
          return finishNotFound(
            false,
            FINISH_NOT_FOUND_FINALIZE_SORT_MODAL_OPEN_ERROR_CODE,
            "Kan inte leverera ut denna artikel."
          );
        } else if (wasAffectedByOffset) {
          return finishNotFound(
            !this.appContext.props.cartState.isSingleCart,
            FINISH_NOT_FOUND_HAS_INPUT_WAS_AFFECTED_BY_OFFSET_ERROR_CODE,
            "ARTIKEL TILLHÖR EJ PRIORITERAD ORDRER, NU FÅR ENDAST PRIORITERADE ORDRAR PACKAS."
          );
        } else {
          var cartId = this.appContext.props.cartState.currentCart.toString();
          cartId = cartId.substring(0, cartId.length - 1);
          SharedFunctions.LogPopupShownEvent({
            modalComponentName: WARNING_MODAL,
            state: JSON.stringify({
              unknown_ean: this.input,
              cartShelfNumber: this.appContext.state.currentShelfNumber,
            }),
            skuId: -1,
            orderNumber: -1,
            quantity: 1,
            cartId: cartId,
          });
          return finishNotFound(
            false,
            FINISH_NOT_FOUND_ARTICLE_DOES_NOT_BELONG_TO_PICKLIST_ERROR_CODE,
            "Artikel hör inte till plocklistan."
          );
        }
      }

      if (wasAffectedByOffset) {
        return finishNotFound(
          true,
          FINISH_NOT_FOUND_WAS_AFFECTED_BY_OFFSET_ERROR_CODE,
          "EJ PRIORITERAD ORDRAR I PLOCKLISTAN, NU FÅR ENDAST PRIORITERADE ORDRAR PACKAS."
        );
      }
    } else {
      if (instruction && instruction.visible) {
        const { desiredTask, message } = instruction;
        if (desiredTask === 25 || desiredTask === 26) {
          this.appContext.setState({
            pendingInstruction: message,
          });
        }
      }
    }

    if (warehouseComment) {
      this.appContext._showWarningModal(
        warehouseComment,
        null,
        "Meddelande",
        "message-to-logistics"
      );
    }
    const allIsScanned = !sortBoxItems.some((sbi) => sbi.verifiedQuantity < sbi.reservedQuantity);
    const hasLockedCartItems = cartContent.cartItems.some(
      (ci) => ci.isLocked && ci.lockedWorkstation > 0
    );

    this.appContext.props.wasCancelled(wasCancelled);
    if (
      !UtilMethods.IsManualPack(this.appContext.props.loginState.workstationType) &&
      shelfNumber == 51 &&
      this.input === "" &&
      this.forcedShelf === -1
    ) {
      this.appContext.setState({
        currentShelfNumber: 51,
        waitingForDeliveryRequest: false,
        currentOrderNumber: -1,
        currentSortBoxID: -1,
        nextAction: ActionTypes.WAITING_FOR_SINGLE_ARTICLE_SCAN,
        forceShelf: null,
      });
    } else
      this.appContext.setState(
        {
          currentSortBoxID: sortBoxID,
          currentShelfNumber: shelfNumber,
          currentOrderNumber: orderNumber,
          boxItems: !isSingle ? sortBoxItems : sortBoxItems.map((sbi, index) => {
             if (index === 0) {
               sbi.verifiedQuantity = 1;
               sbi.partialQuantity = 1;
             }
             return sbi}),
          nextAction: ActionTypes.SCAN_ARTICLE,
          scanDisabled: false,
          finalizeSortModalOpen: false,
          isPaused,
          //halt finalize if we are waithing for weighing
          waitingForDeliveryRequest:
            UtilMethods.IsAutoPack(this.appContext.props.loginState.workstationType) &&
            (allIsScanned || partialDelivery) &&
            !sortBoxItems.some((bi) => bi.requireWeighing),
          scanIDs: scanID > -1 ? [scanID] : [],
          packingPrioDeadlineId: prioDeadlineId,
          cartContent: cartContent,
          sortBoxCompanyID: companyID,
          orderShopID: shopID,
          lastInstruction: instruction,
          forceShelf:
            parseInt(shelfNumber) === this.forcedShelf && !wasCancelled ? this.forcedShelf : null,
          isUrgent,
          notFound,
          sortWorkstationType: workstationType,
          isPartialDelivery: partialDelivery,
          partialDeliveryStarted: partialDelivery,
          partialDeliveryContinued: partialDelivery,
          partialDeliveryAllowed: partialDeliveryAllowed,
          
          //if single get sku from box
          lastScannedSKU: isSingle ? sortBoxItems[0]["skuID"] : -1,
          waitingforMeasurement: false,
          invoiceNumber: invoiceNumber
        },
        () => {
          this.appContext.props.toggleLoading(false);
          if (allIsScanned && sortBoxID > 0 && (this.appContext.props.cartState.currentCart != 6999 || UtilMethods.IsManualPack(this.appContext.props.loginState.workstationType))) {
            this.appContext.setState(
              {
                nextAction: ActionTypes.WAITING_FOR_SINGLE_ARTICLE_SCAN,
              },
              () => {
                if (!this.appContext.state.boxItems.filter((bi) => bi.requireWeighing).length) {
                  if (UtilMethods.IsManualPack(this.appContext.props.loginState.workstationType)) {
                    this.appContext._finalizeBox();
                  }
                } else {
                  this.appContext.setState({ waitingforMeasurement: true });
                }
              }
            );
          } else if (
            UtilMethods.IsManualPack(this.appContext.props.loginState.workstationType) &&
            allIsScanned  && 
            !hasLockedCartItems
          ) {
            this.appContext.setState({
              ...this.appContext._cartState,
              nextAction: ActionTypes.SET_CART,
            });
          } else if (
            UtilMethods.IsManualPack(this.appContext.props.loginState.workstationType) &&
            shelfNumber == 51 &&
            (allIsScanned || this.input === "")
          ) {
            this.appContext._setStateWaitingForSingleArticleScan(); //byt till singel-läge
          }
        }
      );

    this.appContext._refreshSingleItemsGrid(sortBoxID);
  }
}
